import React, { useMemo, useState, useEffect } from "react";
import cloneDeep from "lodash/cloneDeep";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    FormGroup,
    Label,
    Input,
    Row,
    Col,
} from "reactstrap";
import SchemaFromJsonModal from "./SchemaFromJsonModal";

const LEAF_TYPES = ["text", "number", "boolean", "select", "textarea", "date", "json", "color", "file", "tagInput"];
const CONTAINER_TYPES = ["object", "array", "polymorphicArray"];

function createVariantItemSchemaRoot() {
    const o = createDefaultNode("object");
    o.key = "item";
    o.label = "Item";
    return o;
}

function newNodeId() {
    return window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : `n_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

export function createDefaultNode(nodeType) {
    const base = {
        id: newNodeId(),
        key: `field_${Date.now()}`,
        label: "Field",
        required: false,
        defaultValue: null,
        description: "",
        ui: {},
        validation: {},
        conditional: {},
    };
    if (nodeType === "object") {
        return { ...base, nodeType: "object", children: [] };
    }
    if (nodeType === "array") {
        return {
            ...base,
            nodeType: "array",
            itemSchema: {
                id: newNodeId(),
                nodeType: "object",
                key: "item",
                label: "Item",
                required: false,
                defaultValue: null,
                description: "",
                ui: {},
                validation: {},
                conditional: {},
                children: [],
            },
        };
    }
    if (nodeType === "polymorphicArray") {
        return {
            ...base,
            nodeType: "polymorphicArray",
            discriminatorKey: "type",
            variants: [
                {
                    id: newNodeId(),
                    discriminatorValue: "a",
                    label: "Variant a",
                    itemSchema: createVariantItemSchemaRoot(),
                },
            ],
        };
    }
    if (nodeType === "select") {
        return { ...base, nodeType: "select", options: [{ value: "option1", image: "" }] };
    }
    if (nodeType === "color") {
        return { ...base, nodeType: "color", defaultValue: "#000000" };
    }
    if (nodeType === "file") {
        return { ...base, nodeType: "file", ui: { accept: "image/*", sizeHint: "" } };
    }
    if (nodeType === "tagInput") {
        return { ...base, nodeType: "tagInput" };
    }
    return { ...base, nodeType: nodeType || "text" };
}

function findParentPointer(root, targetId, parent = null, pointer = null) {
    if (!root || typeof root !== "object") return null;
    if (root.id === targetId) return { parent, pointer, node: root };
    if (Array.isArray(root.children)) {
        for (let i = 0; i < root.children.length; i++) {
            const ch = root.children[i];
            if (ch.id === targetId) {
                return { parent: root, pointer: { kind: "children", index: i }, node: ch };
            }
            const r = findParentPointer(ch, targetId, root, pointer);
            if (r) return r;
        }
    }
    if (root.nodeType === "polymorphicArray" && Array.isArray(root.variants)) {
        for (let i = 0; i < root.variants.length; i++) {
            const variant = root.variants[i];
            if (!variant) continue;
            if (variant.id === targetId) {
                return { parent: root, pointer: { kind: "polymorphicVariant", variantIndex: i }, node: variant };
            }
            const isch = variant.itemSchema;
            if (isch) {
                if (isch.id === targetId) {
                    return {
                        parent: root,
                        pointer: { kind: "polymorphicVariantItemSchema", variantIndex: i },
                        node: isch,
                    };
                }
                const r = findParentPointer(isch, targetId, isch, pointer);
                if (r) return r;
            }
        }
    }
    if (root.itemSchema) {
        if (root.itemSchema.id === targetId) {
            return { parent: root, pointer: { kind: "itemSchema" }, node: root.itemSchema };
        }
        return findParentPointer(root.itemSchema, targetId, root, pointer);
    }
    return null;
}

function addChildToNode(schemaDoc, parentId, nodeType) {
    const next = cloneDeep(schemaDoc);
    const parentLoc = findParentPointer(next.root, parentId);
    if (!parentLoc || !parentLoc.node) return next;
    const parent = parentLoc.node;
    const child = createDefaultNode(nodeType);
    if (parent.nodeType === "array") {
        if (!parent.itemSchema) {
            parent.itemSchema = createDefaultNode("object");
            parent.itemSchema.key = "item";
        }
        if (!parent.itemSchema.children) parent.itemSchema.children = [];
        parent.itemSchema.children.push(child);
        return next;
    }
    if (parent.nodeType === "object") {
        if (!parent.children) parent.children = [];
        parent.children.push(child);
        return next;
    }
    return next;
}

/** Append already-validated nodes to an object node. Additive only; an existing key is skipped. */
function addNodesToObject(schemaDoc, targetId, nodes) {
    const next = cloneDeep(schemaDoc);
    const loc = findParentPointer(next.root, targetId);
    if (!loc || !loc.node || loc.node.nodeType !== "object") return next;
    if (!loc.node.children) loc.node.children = [];
    const taken = new Set(loc.node.children.map((c) => c.key));
    nodes.forEach((n) => {
        if (taken.has(n.key)) return;
        taken.add(n.key);
        loc.node.children.push(cloneDeep(n));
    });
    return next;
}

/** Dotted path of a node, for labelling the JSON import target. */
function pathToNodeId(node, targetId, path = "root") {
    if (!node || typeof node !== "object") return null;
    if (node.id === targetId) return path;
    if (Array.isArray(node.children)) {
        for (const ch of node.children) {
            const hit = pathToNodeId(ch, targetId, `${path}.${ch.key}`);
            if (hit) return hit;
        }
    }
    if (node.nodeType === "array" && node.itemSchema) {
        const hit = pathToNodeId(node.itemSchema, targetId, `${path}[]`);
        if (hit) return hit;
    }
    if (node.nodeType === "polymorphicArray" && Array.isArray(node.variants)) {
        for (const v of node.variants) {
            if (!v || !v.itemSchema) continue;
            const hit = pathToNodeId(v.itemSchema, targetId, `${path}[${node.discriminatorKey}=${v.discriminatorValue}]`);
            if (hit) return hit;
        }
    }
    return null;
}

function removeNodeById(schemaDoc, targetId) {
    if (schemaDoc.root && schemaDoc.root.id === targetId) return schemaDoc;
    const next = cloneDeep(schemaDoc);
    const loc = findParentPointer(next.root, targetId);
    if (!loc || !loc.parent || !loc.pointer) return next;
    if (loc.pointer.kind === "children") {
        loc.parent.children.splice(loc.pointer.index, 1);
    }
    if (loc.pointer.kind === "itemSchema") {
        loc.parent.itemSchema = null;
    }
    if (loc.pointer.kind === "polymorphicVariant") {
        const vars = loc.parent.variants;
        if (Array.isArray(vars) && vars.length > 1) {
            vars.splice(loc.pointer.variantIndex, 1);
        }
    }
    if (loc.pointer.kind === "polymorphicVariantItemSchema") {
        const vars = loc.parent.variants;
        if (Array.isArray(vars) && vars[loc.pointer.variantIndex]) {
            vars[loc.pointer.variantIndex].itemSchema = createVariantItemSchemaRoot();
        }
    }
    return next;
}

function updateNodeById(schemaDoc, targetId, patch) {
    const next = cloneDeep(schemaDoc);
    const loc = findParentPointer(next.root, targetId);
    if (!loc || !loc.node) return next;
    Object.assign(loc.node, patch);
    if (patch.nodeType) {
        loc.node.defaultValue = null;
        if (CONTAINER_TYPES.includes(patch.nodeType)) {
            if (patch.nodeType === "object") {
                delete loc.node.itemSchema;
                delete loc.node.variants;
                delete loc.node.discriminatorKey;
                if (!loc.node.children) loc.node.children = [];
            }
            if (patch.nodeType === "array") {
                delete loc.node.variants;
                delete loc.node.discriminatorKey;
                delete loc.node.children;
                if (!loc.node.itemSchema) {
                    loc.node.itemSchema = createDefaultNode("object");
                    loc.node.itemSchema.key = "item";
                }
            }
            if (patch.nodeType === "polymorphicArray") {
                delete loc.node.itemSchema;
                delete loc.node.children;
                if (typeof loc.node.discriminatorKey !== "string" || !String(loc.node.discriminatorKey).trim()) {
                    loc.node.discriminatorKey = "type";
                }
                if (!Array.isArray(loc.node.variants) || loc.node.variants.length === 0) {
                    loc.node.variants = [
                        {
                            id: newNodeId(),
                            discriminatorValue: "a",
                            label: "Variant a",
                            itemSchema: createVariantItemSchemaRoot(),
                        },
                    ];
                }
            }
        } else if (LEAF_TYPES.includes(patch.nodeType)) {
            delete loc.node.children;
            delete loc.node.itemSchema;
            delete loc.node.variants;
            delete loc.node.discriminatorKey;
            if (patch.nodeType === "select" && !patch.optionsSource && !Array.isArray(loc.node.options)) loc.node.options = [{ value: "option1", image: "" }];
            if (patch.nodeType === "color") loc.node.defaultValue = "#000000";
            if (patch.nodeType === "file" && (!loc.node.ui || !loc.node.ui.accept)) {
                loc.node.ui = { ...(loc.node.ui || {}), accept: "image/*" };
            }
        }
    }
    if (loc.node.nodeType === "select" && !loc.node.optionsSource && !Array.isArray(loc.node.options)) {
        loc.node.options = [{ value: "option1", image: "" }];
    }
    return next;
}

function addPolymorphicVariant(schemaDoc, polymorphicNodeId) {
    const next = cloneDeep(schemaDoc);
    const loc = findParentPointer(next.root, polymorphicNodeId);
    if (!loc || !loc.node || loc.node.nodeType !== "polymorphicArray") return next;
    if (!Array.isArray(loc.node.variants)) loc.node.variants = [];
    const n = loc.node.variants.length + 1;
    loc.node.variants.push({
        id: newNodeId(),
        discriminatorValue: `v${n}`,
        label: "",
        itemSchema: createVariantItemSchemaRoot(),
    });
    return next;
}

const TreeBranch = ({ node, depth, selectedId, expanded, toggleExpand, onSelect }) => {
    if (!node) return null;
    const isSel = node.id === selectedId;
    const isOpen = expanded[node.id] !== false;
    const polyVariants = node.nodeType === "polymorphicArray" && Array.isArray(node.variants) ? node.variants : [];
    const hasKids =
        (node.children && node.children.length) ||
        (node.nodeType === "array" && node.itemSchema) ||
        (node.nodeType === "polymorphicArray" && polyVariants.length > 0);
    return (
        <div style={{ marginLeft: depth * 14, marginTop: 4 }}>
            <div
                style={{
                    cursor: "pointer",
                    padding: "2px 4px",
                    background: isSel ? "#e3f2fd" : "transparent",
                    borderRadius: 4,
                    whiteSpace: "nowrap",
                }}
                onClick={() => onSelect(node.id)}
            >
                {hasKids ? (
                    <span onClick={(e) => { e.stopPropagation(); toggleExpand(node.id); }} style={{ marginRight: 6 }}>
                        {isOpen ? "▼" : "▶"}
                    </span>
                ) : (
                    <span style={{ marginRight: 18 }} />
                )}
                <strong>{node.key || "?"}</strong>{" "}
                <span style={{ color: "#666" }}>({node.nodeType})</span>
                {node.label ? ` — ${node.label}` : ""}
            </div>
            {hasKids && isOpen && node.nodeType === "array" && node.itemSchema && (
                <TreeBranch
                    node={node.itemSchema}
                    depth={depth + 1}
                    selectedId={selectedId}
                    expanded={expanded}
                    toggleExpand={toggleExpand}
                    onSelect={onSelect}
                />
            )}
            {hasKids &&
                isOpen &&
                node.nodeType === "polymorphicArray" &&
                polyVariants.map((variant) => (
                    <div key={variant.id} style={{ marginLeft: 0 }}>
                        <div
                            style={{
                                cursor: "pointer",
                                padding: "2px 4px",
                                marginTop: 2,
                                marginLeft: (depth + 1) * 14,
                                background: variant.id === selectedId ? "#e3f2fd" : "transparent",
                                borderRadius: 4,
                                whiteSpace: "nowrap",
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                onSelect(variant.id);
                            }}
                        >
                            <span style={{ color: "#666" }}>[variant]</span>{" "}
                            <strong>{String(variant.discriminatorValue)}</strong>
                            {variant.label ? ` — ${variant.label}` : ""}
                        </div>
                        {variant.itemSchema && (
                            <TreeBranch
                                node={variant.itemSchema}
                                depth={depth + 2}
                                selectedId={selectedId}
                                expanded={expanded}
                                toggleExpand={toggleExpand}
                                onSelect={onSelect}
                            />
                        )}
                    </div>
                ))}
            {hasKids &&
                isOpen &&
                node.nodeType === "object" &&
                (node.children || []).map((ch) => (
                    <TreeBranch
                        key={ch.id}
                        node={ch}
                        depth={depth + 1}
                        selectedId={selectedId}
                        expanded={expanded}
                        toggleExpand={toggleExpand}
                        onSelect={onSelect}
                    />
                ))}
        </div>
    );
};

const SchemaBuilderCore = ({
    schema,
    setSchema,
    selectedNodeId,
    setSelectedNodeId,
    expanded,
    setExpanded,
}) => {
    const selectedPointer = useMemo(() => {
        if (!schema || !schema.root || !selectedNodeId) return null;
        return findParentPointer(schema.root, selectedNodeId);
    }, [schema, selectedNodeId]);

    const selectedNode = useMemo(() => {
        if (!selectedPointer) return null;
        return selectedPointer.node;
    }, [selectedPointer]);

    const isPolymorphicVariant = selectedPointer?.pointer?.kind === "polymorphicVariant";
    const polymorphicVariantCount =
        isPolymorphicVariant && Array.isArray(selectedPointer.parent?.variants)
            ? selectedPointer.parent.variants.length
            : 0;

    const [jsonDefaultDraft, setJsonDefaultDraft] = useState("");
    const [newOptionDraft, setNewOptionDraft] = useState("");
    const [jsonImportOpen, setJsonImportOpen] = useState(false);

    const selectedPath = useMemo(() => {
        if (!schema || !schema.root || !selectedNodeId) return null;
        return pathToNodeId(schema.root, selectedNodeId);
    }, [schema, selectedNodeId]);

    useEffect(() => {
        setNewOptionDraft("");
    }, [selectedNodeId]);

    useEffect(() => {
        if (!selectedNode || selectedNode.nodeType !== "json") {
            setJsonDefaultDraft("");
            return;
        }
        const v = selectedNode.defaultValue;
        if (v != null && typeof v === "object" && !Array.isArray(v)) {
            try {
                setJsonDefaultDraft(JSON.stringify(v, null, 2));
            } catch (e) {
                setJsonDefaultDraft("");
            }
        } else {
            setJsonDefaultDraft("");
        }
    }, [selectedNodeId, selectedNode?.nodeType, selectedNode?.defaultValue]);

    const toggleExpand = (id) => {
        setExpanded((e) => ({ ...e, [id]: e[id] === false ? true : false }));
    };

    const addChild = (nodeType) => {
        if (!schema || !schema.root) return;
        const parentId = selectedNodeId || schema.root.id;
        const loc = findParentPointer(schema.root, parentId);
        if (!loc || !loc.node) return;
        const n = loc.node;
        if (n.nodeType !== "object" && n.nodeType !== "array") return;
        setSchema(addChildToNode(schema, parentId, nodeType));
    };

    const removeSelected = () => {
        if (!schema || !selectedNodeId || schema.root.id === selectedNodeId) return;
        setSchema(removeNodeById(schema, selectedNodeId));
        setSelectedNodeId(schema.root.id);
    };

    const applyJsonNodes = (nodes) => {
        if (!schema || !selectedNode || selectedNode.nodeType !== "object" || !nodes.length) return;
        setSchema(addNodesToObject(schema, selectedNode.id, nodes));
        setExpanded((e) => ({ ...e, [selectedNode.id]: true }));
    };

    const updateSelectedField = (field, value) => {
        if (!selectedNode || (selectedNode.id === schema.root.id && field === "key")) return;
        setSchema(updateNodeById(schema, selectedNode.id, { [field]: value }));
    };

    // Helpers for select options list editing
    const addSelectOption = () => {
        const trimmed = newOptionDraft.trim();
        if (!trimmed) return;
        const opts = selectedNode?.options || [];
        if (opts.some((o) => String(o.value) === trimmed)) return; // no duplicates
        updateSelectedField("options", [...opts, { value: trimmed, image: "" }]);
        setNewOptionDraft("");
    };

    const removeSelectOption = (index) => {
        const opts = [...(selectedNode?.options || [])];
        const removed = opts[index];
        opts.splice(index, 1);
        updateSelectedField("options", opts);
        if (selectedNode?.defaultValue != null && String(selectedNode.defaultValue) === String(removed.value)) {
            updateSelectedField("defaultValue", null);
        }
    };

    const updateSelectOption = (index, newVal) => {
        const trimmed = newVal.trim();
        if (!trimmed) return;
        const opts = [...(selectedNode?.options || [])];
        opts[index] = { value: trimmed, image: opts[index].image };
        updateSelectedField("options", opts);
    };

    const updateSelectedUi = (key, value) => {
        const ui = { ...(selectedNode?.ui || {}) };
        if (value === "" || value == null) delete ui[key];
        else ui[key] = value;
        updateSelectedField("ui", ui);
    };

    const updateSelectedValidation = (key, value) => {
        const validation = { ...(selectedNode?.validation || {}) };
        if (value === "" || value == null) delete validation[key];
        else validation[key] = value;
        updateSelectedField("validation", validation);
    };

    const updateSelectOptionImage = (index, url) => {
        const opts = [...(selectedNode?.options || [])];
        opts[index] = { value: opts[index].value, image: url };
        updateSelectedField("options", opts);
    };

    if (!schema || !schema.root) {
        return <p>No schema loaded.</p>;
    }

    return (
        <Row>
            <Col md="5">
                <Card>
                    <CardHeader>Schema tree</CardHeader>
                    <CardBody style={{ maxHeight: 480, overflow: "auto" }}>
                        <TreeBranch
                            node={schema.root}
                            depth={0}
                            selectedId={selectedNodeId}
                            expanded={expanded}
                            toggleExpand={toggleExpand}
                            onSelect={setSelectedNodeId}
                        />
                    </CardBody>
                </Card>
                <Card className="mt-3">
                    <CardHeader>Add node</CardHeader>
                    <CardBody>
                        <p className="small text-muted">
                            Select object, array, or polymorphic array, then add children on the object (or array item
                            object).
                        </p>
                        <p className="small text-muted">
                            Arrays share one item shape. Polymorphic arrays use a discriminator key (e.g. type) and
                            one item schema per variant value.
                        </p>
                        <div>
                            {CONTAINER_TYPES.map((t) => (
                                <Button key={t} size="sm" color="secondary" className="mr-1 mb-1" onClick={() => addChild(t)}>
                                    + {t}
                                </Button>
                            ))}
                        </div>
                        <div className="mt-2">
                            {LEAF_TYPES.map((t) => (
                                <Button key={t} size="sm" outline className="mr-1 mb-1" onClick={() => addChild(t)}>
                                    + {t}
                                </Button>
                            ))}
                        </div>
                        {selectedNode && selectedNode.nodeType === "object" && (
                            <div className="mt-2">
                                <Button size="sm" color="info" outline onClick={() => setJsonImportOpen(true)}>
                                    Create from JSON
                                </Button>
                                <p className="small text-muted mt-1 mb-0">
                                    Paste a ready-made set of fields to add under <code>{selectedPath}</code>. Adds
                                    only — it never changes what is already there.
                                </p>
                            </div>
                        )}
                        <Button size="sm" color="danger" outline className="mt-2" onClick={removeSelected}>
                            Remove selected
                        </Button>
                        {selectedNode && selectedNode.nodeType === "object" && jsonImportOpen && (
                            <SchemaFromJsonModal
                                isOpen={jsonImportOpen}
                                toggle={() => setJsonImportOpen(false)}
                                targetNode={selectedNode}
                                targetPath={selectedPath}
                                validTypes={CONTAINER_TYPES.concat(LEAF_TYPES)}
                                onApply={applyJsonNodes}
                            />
                        )}
                    </CardBody>
                </Card>
            </Col>
            <Col md="7">
                <Card>
                    <CardHeader>Node inspector</CardHeader>
                    <CardBody>
                        {!selectedNode ? (
                            <p>Select a node</p>
                        ) : isPolymorphicVariant ? (
                            <>
                                <p className="small text-muted">
                                    Variant: one item schema for this discriminator value. Add fields under the nested
                                    Item node in the tree.
                                </p>
                                <FormGroup>
                                    <Label>Variant label</Label>
                                    <Input
                                        value={selectedNode.label || ""}
                                        onChange={(e) => updateSelectedField("label", e.target.value)}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label>Discriminator value</Label>
                                    <Input
                                        value={
                                            selectedNode.discriminatorValue == null
                                                ? ""
                                                : String(selectedNode.discriminatorValue)
                                        }
                                        style={String(selectedNode.discriminatorValue ?? "").trim() === "" ? { borderColor: "#dc3545" } : {}}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            if (raw === "") {
                                                updateSelectedField("discriminatorValue", "");
                                                return;
                                            }
                                            if (/^-?\d+(\.\d+)?$/.test(raw)) {
                                                const num = Number(raw);
                                                updateSelectedField(
                                                    "discriminatorValue",
                                                    Number.isNaN(num) ? raw : num
                                                );
                                            } else if (raw === "true") {
                                                updateSelectedField("discriminatorValue", true);
                                            } else if (raw === "false") {
                                                updateSelectedField("discriminatorValue", false);
                                            } else {
                                                updateSelectedField("discriminatorValue", raw);
                                            }
                                        }}
                                    />
                                    {String(selectedNode.discriminatorValue ?? "").trim() === "" && (
                                        <div style={{ color: "#dc3545", fontSize: "0.78rem", marginTop: 2 }}>
                                            Discriminator value cannot be empty.
                                        </div>
                                    )}
                                </FormGroup>
                                {polymorphicVariantCount > 1 && (
                                    <p className="small text-muted">
                                        You can remove this variant with Remove selected (at least one variant must
                                        remain).
                                    </p>
                                )}
                            </>
                        ) : (
                            <>
                                <FormGroup>
                                    <Label>Key</Label>
                                    <Input
                                        value={selectedNode.key || ""}
                                        disabled={selectedNode.id === schema.root.id}
                                        invalid={!selectedNode.id === schema.root.id && !!(selectedNode.key || "").trim() === false && selectedNode.id !== schema.root.id}
                                        style={selectedNode.id !== schema.root.id && !(selectedNode.key || "").trim() ? { borderColor: "#dc3545" } : {}}
                                        onChange={(e) => updateSelectedField("key", e.target.value)}
                                    />
                                    {selectedNode.id !== schema.root.id && !(selectedNode.key || "").trim() && (
                                        <div style={{ color: "#dc3545", fontSize: "0.78rem", marginTop: 2 }}>
                                            Key is required — nodes without a key will be ignored on save.
                                        </div>
                                    )}
                                </FormGroup>
                                <FormGroup>
                                    <Label>Label</Label>
                                    <Input
                                        value={selectedNode.label || ""}
                                        onChange={(e) => updateSelectedField("label", e.target.value)}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label>Node type</Label>
                                    <Input
                                        type="select"
                                        value={selectedNode.nodeType || "text"}
                                        disabled={selectedNode.id === schema.root.id || selectedPointer?.pointer?.kind === "polymorphicVariantItemSchema"}
                                        onChange={(e) => updateSelectedField("nodeType", e.target.value)}
                                    >
                                        <option value="object">object</option>
                                        <option value="array">array</option>
                                        <option value="polymorphicArray">polymorphicArray</option>
                                        {LEAF_TYPES.map((t) => (
                                            <option key={t} value={t}>
                                                {t}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                                <FormGroup
                                    check
                                    className="mb-3"
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => e.stopPropagation()}
                                >
                                    <Label check>
                                        <Input
                                            id={`schema-node-required-${selectedNode.id}`}
                                            type="checkbox"
                                            checked={!!selectedNode.required}
                                            onChange={(e) => updateSelectedField("required", e.target.checked)}
                                        />
                                        <span className="form-check-sign" />
                                        Required
                                    </Label>
                                </FormGroup>
                                <FormGroup>
                                    <Label>Description</Label>
                                    <Input
                                        type="textarea"
                                        rows={2}
                                        value={selectedNode.description || ""}
                                        onChange={(e) => updateSelectedField("description", e.target.value)}
                                    />
                                </FormGroup>
                                {selectedNode.nodeType === "polymorphicArray" && (
                                    <>
                                        <FormGroup>
                                            <Label>Discriminator key</Label>
                                            <Input
                                                value={selectedNode.discriminatorKey || ""}
                                                placeholder="type"
                                                style={!(selectedNode.discriminatorKey || "").trim() ? { borderColor: "#dc3545" } : {}}
                                                onChange={(e) =>
                                                    updateSelectedField("discriminatorKey", e.target.value)
                                                }
                                            />
                                            {!(selectedNode.discriminatorKey || "").trim() && (
                                                <div style={{ color: "#dc3545", fontSize: "0.78rem", marginTop: 2 }}>
                                                    Discriminator key is required (e.g. "type").
                                                </div>
                                            )}
                                        </FormGroup>
                                        <FormGroup>
                                            <Button
                                                size="sm"
                                                color="secondary"
                                                outline
                                                type="button"
                                                onClick={() =>
                                                    setSchema(addPolymorphicVariant(schema, selectedNode.id))
                                                }
                                            >
                                                Add variant
                                            </Button>
                                            <p className="small text-muted mt-2 mb-0">
                                                Each variant maps one discriminator value to an item object schema.
                                                At least one variant is required.
                                            </p>
                                        </FormGroup>
                                    </>
                                )}
                                {selectedNode.nodeType === "select" && (
                                    <FormGroup>
                                        <Label>Where options come from</Label>
                                        <Input
                                            type="select"
                                            value={selectedNode.optionsSource || ""}
                                            onChange={(e) => updateSelectedField("optionsSource", e.target.value || undefined)}
                                        >
                                            <option value="">Typed in by hand</option>
                                            <option value="app_categories">App categories (per project)</option>
                                            <option value="ctv_categories">CTV categories (per project)</option>
                                        </Input>
                                        <p className="small text-muted mt-1 mb-0">
                                            A per-project source fills the dropdown from that project's categories when the
                                            publisher opens it, and stores the whole category object under this key.
                                        </p>
                                    </FormGroup>
                                )}
                                {selectedNode.nodeType === "select" && !selectedNode.optionsSource && (
                                    <FormGroup>
                                        <Label>Options</Label>
                                        {/* Per-option list with inline edit + remove */}
                                        {(selectedNode.options || []).length === 0 && (
                                            <div style={{ color: "#dc3545", fontSize: "0.78rem", marginBottom: 6 }}>
                                                At least one option is required.
                                            </div>
                                        )}
                                        <div style={{ marginBottom: 6 }}>
                                            {(selectedNode.options || []).map((opt, idx) => (
                                                <div
                                                    key={idx}
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 6,
                                                        marginBottom: 4,
                                                    }}
                                                >
                                                    <Input
                                                        style={{ flex: 1 }}
                                                        value={opt.value}
                                                        placeholder={`Option ${idx + 1}`}
                                                        onChange={(e) => updateSelectOption(idx, e.target.value)}
                                                        onBlur={(e) => {
                                                            const trimmed = e.target.value.trim();
                                                            if (!trimmed) removeSelectOption(idx);
                                                        }}
                                                    />
                                                    <Input
                                                        style={{ flex: 1.5 }}
                                                        value={opt.image}
                                                        placeholder="Image URL (optional)"
                                                        onChange={(e) => updateSelectOptionImage(idx, e.target.value)}
                                                    />
                                                    <Button
                                                        size="sm"
                                                        color="danger"
                                                        type="button"
                                                        style={{
                                                            flexShrink: 0,
                                                            backgroundColor: "#dc3545",
                                                            borderColor: "#dc3545",
                                                            color: "#fff",
                                                            fontWeight: "bold",
                                                            lineHeight: 1,
                                                        }}
                                                        onClick={() => removeSelectOption(idx)}
                                                        title="Remove option"
                                                    >
                                                        ✕
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                        {/* Add new option row */}
                                        <div style={{ display: "flex", gap: 6 }}>
                                            <Input
                                                style={{ flex: 1 }}
                                                placeholder="New option value…"
                                                value={newOptionDraft}
                                                onChange={(e) => setNewOptionDraft(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        addSelectOption();
                                                    }
                                                }}
                                            />
                                            <Button
                                                size="sm"
                                                color="primary"
                                                type="button"
                                                style={{ flexShrink: 0, opacity: !newOptionDraft.trim() ? 0.5 : 1 }}
                                                onClick={addSelectOption}
                                                disabled={!newOptionDraft.trim()}
                                            >
                                                + Add
                                            </Button>
                                        </div>
                                        <div style={{ color: "#6c757d", fontSize: "0.75rem", marginTop: 4 }}>
                                            Press Enter or click Add. Duplicates are ignored.
                                        </div>
                                    </FormGroup>
                                )}
                                {selectedNode.nodeType === "text" && (
                                    <FormGroup>
                                        <Label>Format</Label>
                                        <Input
                                            type="select"
                                            value={selectedNode.validation?.format || ""}
                                            onChange={(e) => updateSelectedValidation("format", e.target.value)}
                                        >
                                            <option value="">plain text</option>
                                            <option value="url">URL (http/https)</option>
                                        </Input>
                                        <p className="small text-muted mt-1 mb-0">
                                            URL format is enforced on save and shown inline while editing.
                                        </p>
                                    </FormGroup>
                                )}
                                {selectedNode.nodeType === "textarea" && (
                                    <>
                                        <FormGroup
                                            check
                                            className="mb-3"
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={(e) => e.stopPropagation()}
                                        >
                                            <Label check>
                                                <Input
                                                    id={`schema-node-monospace-${selectedNode.id}`}
                                                    type="checkbox"
                                                    checked={!!selectedNode.ui?.monospace}
                                                    onChange={(e) =>
                                                        updateSelectedUi("monospace", e.target.checked ? true : "")
                                                    }
                                                />
                                                <span className="form-check-sign" />
                                                Code field (monospace)
                                            </Label>
                                            <p className="small text-muted mt-1 mb-0">
                                                For CSS, ad codes, robots.txt and similar raw snippets.
                                            </p>
                                        </FormGroup>
                                        <FormGroup>
                                            <Label>Rows</Label>
                                            <Input
                                                type="number"
                                                min={1}
                                                placeholder={selectedNode.ui?.monospace ? "8" : "3"}
                                                value={selectedNode.ui?.rows == null ? "" : selectedNode.ui.rows}
                                                onChange={(e) => {
                                                    const raw = e.target.value;
                                                    if (raw === "") {
                                                        updateSelectedUi("rows", "");
                                                        return;
                                                    }
                                                    const num = Number(raw);
                                                    updateSelectedUi("rows", Number.isNaN(num) || num < 1 ? 1 : num);
                                                }}
                                            />
                                        </FormGroup>
                                    </>
                                )}
                                {selectedNode.nodeType === "file" && (
                                    <>
                                        <FormGroup>
                                            <Label>Accepted file types</Label>
                                            <Input
                                                placeholder="image/png,image/jpeg,image/webp"
                                                value={selectedNode.ui?.accept || ""}
                                                onChange={(e) => updateSelectedUi("accept", e.target.value)}
                                            />
                                            <p className="small text-muted mt-1 mb-0">
                                                Passed straight to the file picker. Blank allows any type.
                                            </p>
                                        </FormGroup>
                                        <FormGroup>
                                            <Label>Size hint</Label>
                                            <Input
                                                placeholder="16x16"
                                                value={selectedNode.ui?.sizeHint || ""}
                                                onChange={(e) => updateSelectedUi("sizeHint", e.target.value)}
                                            />
                                            <p className="small text-muted mt-1 mb-0">
                                                Shown next to the label. Advisory only — not enforced.
                                            </p>
                                        </FormGroup>
                                    </>
                                )}
                                {selectedNode.nodeType === "tagInput" && (
                                    <FormGroup>
                                        <Label>Max tags</Label>
                                        <Input
                                            type="number"
                                            min={1}
                                            placeholder="unlimited"
                                            value={selectedNode.validation?.max == null ? "" : selectedNode.validation.max}
                                            onChange={(e) => {
                                                const raw = e.target.value;
                                                if (raw === "") {
                                                    updateSelectedValidation("max", "");
                                                    return;
                                                }
                                                const num = Number(raw);
                                                updateSelectedValidation("max", Number.isNaN(num) || num < 1 ? 1 : num);
                                            }}
                                        />
                                        <p className="small text-muted mt-1 mb-0">
                                            Publishers type free-form values; saved as an array of strings.
                                        </p>
                                    </FormGroup>
                                )}
                                {selectedNode.nodeType &&
                                    LEAF_TYPES.includes(selectedNode.nodeType) &&
                                    (() => {
                                        const nt = selectedNode.nodeType;
                                        const reqId = `schema-node-default-${selectedNode.id}`;
                                        if (nt === "boolean") {
                                            const dv = selectedNode.defaultValue;
                                            const sel =
                                                dv === true ? "true" : dv === false ? "false" : "";
                                            return (
                                                <FormGroup>
                                                    <Label for={reqId}>Default value</Label>
                                                    <Input
                                                        id={reqId}
                                                        type="select"
                                                        value={sel}
                                                        onChange={(e) => {
                                                            const v = e.target.value;
                                                            updateSelectedField(
                                                                "defaultValue",
                                                                v === "" ? null : v === "true"
                                                            );
                                                        }}
                                                    >
                                                        <option value="">(none)</option>
                                                        <option value="true">true</option>
                                                        <option value="false">false</option>
                                                    </Input>
                                                </FormGroup>
                                            );
                                        }
                                        if (nt === "number") {
                                            const n = selectedNode.defaultValue;
                                            return (
                                                <FormGroup>
                                                    <Label for={reqId}>Default value</Label>
                                                    <Input
                                                        id={reqId}
                                                        type="number"
                                                        min={0}
                                                        value={n == null || n === "" ? "" : n}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "-" || e.key === "e" || e.key === "E") {
                                                                e.preventDefault();
                                                            }
                                                        }}
                                                        onChange={(e) => {
                                                            const s = e.target.value;
                                                            if (s === "")
                                                                updateSelectedField("defaultValue", null);
                                                            else {
                                                                const num = Number(s);
                                                                updateSelectedField(
                                                                    "defaultValue",
                                                                    Number.isNaN(num) ? null : num < 0 ? 0 : num
                                                                );
                                                            }
                                                        }}
                                                        onBlur={(e) => {
                                                            const num = Number(e.target.value);
                                                            if (!Number.isNaN(num) && num < 0) {
                                                                updateSelectedField("defaultValue", 0);
                                                            }
                                                        }}
                                                    />
                                                </FormGroup>
                                            );
                                        }
                                        if (nt === "color") {
                                            const expandShortHex = (hex) => {
                                                if (!hex || typeof hex !== "string") return "#000000";
                                                const t = hex.trim();
                                                const m = t.match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
                                                if (!m) return "#000000";
                                                if (m[1].length === 3) {
                                                    const [r, g, b] = m[1].split("");
                                                    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
                                                }
                                                return `#${m[1].toLowerCase()}`;
                                            };
                                            const pickerVal = expandShortHex(
                                                selectedNode.defaultValue == null
                                                    ? ""
                                                    : String(selectedNode.defaultValue)
                                            );
                                            const textVal =
                                                selectedNode.defaultValue == null
                                                    ? ""
                                                    : String(selectedNode.defaultValue);
                                            return (
                                                <FormGroup>
                                                    <Label for={reqId}>Default value</Label>
                                                    <div className="d-flex align-items-center flex-wrap">
                                                        <Input
                                                            id={reqId}
                                                            type="color"
                                                            className="mr-2 mb-1"
                                                            style={{ width: 48, height: 32, padding: 2 }}
                                                            value={pickerVal}
                                                            onChange={(e) => {
                                                                updateSelectedField(
                                                                    "defaultValue",
                                                                    e.target.value.toLowerCase()
                                                                );
                                                            }}
                                                        />
                                                        <Input
                                                            type="text"
                                                            className="mb-1"
                                                            style={{ maxWidth: 120 }}
                                                            placeholder="#000000"
                                                            value={textVal}
                                                            onChange={(e) => {
                                                                const t = e.target.value.trim();
                                                                if (t === "")
                                                                    updateSelectedField("defaultValue", null);
                                                                else updateSelectedField("defaultValue", t);
                                                            }}
                                                        />
                                                    </div>
                                                </FormGroup>
                                            );
                                        }
                                        if (nt === "select") {
                                            const opts = selectedNode.options || [];
                                            const cur = selectedNode.defaultValue;
                                            const strVal =
                                                cur != null &&
                                                (typeof cur === "string" ||
                                                    typeof cur === "number" ||
                                                    typeof cur === "boolean")
                                                    ? String(cur)
                                                    : "";
                                            const allowed = new Set(opts.map((o) => String(o.value)));
                                            const safeVal = allowed.has(strVal) ? strVal : "";
                                            const selectedOpt = opts.find((o) => String(o.value) === safeVal);
                                            const selectedTitle = selectedOpt == null ? "" : String(selectedOpt.value);
                                            return (
                                                <FormGroup>
                                                    <Label for={reqId}>Default value</Label>
                                                    <Input
                                                        id={reqId}
                                                        type="select"
                                                        title={selectedTitle}
                                                        style={{ maxWidth: "100%", whiteSpace: "nowrap", textOverflow: "ellipsis" }}
                                                        value={safeVal}
                                                        onChange={(e) => {
                                                            const v = e.target.value;
                                                            if (v === "")
                                                                updateSelectedField("defaultValue", null);
                                                            else {
                                                                const orig = opts.find((o) => String(o.value) === v);
                                                                updateSelectedField(
                                                                    "defaultValue",
                                                                    orig !== undefined ? orig.value : v
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        <option value="">(none)</option>
                                                        {opts.map((o) => {
                                                            const full = String(o.value);
                                                            const truncated =
                                                                full.length > 80 ? `${full.slice(0, 77)}...` : full;
                                                            return (
                                                                <option key={full} value={full} title={full}>
                                                                    {truncated}
                                                                </option>
                                                            );
                                                        })}
                                                    </Input>
                                                </FormGroup>
                                            );
                                        }
                                        if (nt === "json") {
                                            return (
                                                <FormGroup>
                                                    <Label>Default value (JSON object)</Label>
                                                    <Input
                                                        type="textarea"
                                                        rows={5}
                                                        placeholder="{}"
                                                        value={jsonDefaultDraft}
                                                        onChange={(e) => {
                                                            const raw = e.target.value;
                                                            setJsonDefaultDraft(raw);
                                                            if (raw.trim() === "") {
                                                                updateSelectedField("defaultValue", null);
                                                                return;
                                                            }
                                                            try {
                                                                const parsed = JSON.parse(raw);
                                                                if (
                                                                    parsed &&
                                                                    typeof parsed === "object" &&
                                                                    !Array.isArray(parsed)
                                                                )
                                                                    updateSelectedField("defaultValue", parsed);
                                                            } catch (err) {
                                                                /* incomplete JSON while typing */
                                                            }
                                                        }}
                                                    />
                                                </FormGroup>
                                            );
                                        }
                                        const strTypes = ["text", "textarea", "date"];
                                        if (strTypes.includes(nt)) {
                                            const s =
                                                selectedNode.defaultValue == null
                                                    ? ""
                                                    : String(selectedNode.defaultValue);
                                            const rows = nt === "textarea" ? 3 : 1;
                                            return (
                                                <FormGroup>
                                                    <Label for={reqId}>Default value</Label>
                                                    <Input
                                                        id={reqId}
                                                        type={nt === "textarea" ? "textarea" : "text"}
                                                        rows={rows}
                                                        value={s}
                                                        onChange={(e) => {
                                                            const t = e.target.value;
                                                            updateSelectedField(
                                                                "defaultValue",
                                                                t === "" ? null : t
                                                            );
                                                        }}
                                                    />
                                                </FormGroup>
                                            );
                                        }
                                        return null;
                                    })()}
                            </>
                        )}
                    </CardBody>
                </Card>
                <Card className="mt-3">
                    <CardHeader>Template meta</CardHeader>
                    <CardBody>
                        <FormGroup>
                            <Label>Template name</Label>
                            <Input
                                value={schema.templateName || ""}
                                style={!(schema.templateName || "").trim() ? { borderColor: "#dc3545" } : {}}
                                onChange={(e) =>
                                    setSchema({ ...schema, templateName: e.target.value })
                                }
                            />
                            {!(schema.templateName || "").trim() && (
                                <div style={{ color: "#dc3545", fontSize: "0.78rem", marginTop: 2 }}>
                                    Template name is required.
                                </div>
                            )}
                        </FormGroup>
                        <FormGroup>
                            <Label>Template key (read-only)</Label>
                            <Input value={schema.templateKey || ""} disabled readOnly />
                        </FormGroup>
                    </CardBody>
                </Card>
            </Col>
        </Row>
    );
};

export default SchemaBuilderCore;
export { findParentPointer, newNodeId };
